import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { api } from '../../api/repository'
import { Button, ScreenHeader } from '../../ui/primitives'
import { Segmented, TextField } from '../../ui/fields'
import { useApp } from '../../state/AppState'
import styles from './auth.module.css'

type Mode = 'login' | 'register'

const MODES: { value: Mode; title: string }[] = [
  { value: 'login', title: 'Вход' },
  { value: 'register', title: 'Регистрация' }
]

/**
 * Вход и регистрация. В отличие от nox-assist здесь нет корпоративного
 * SSO: коллекционеры регистрируются сами, логином и паролем.
 *
 * Обе формы на одном экране переключателем: это один и тот же разговор
 * с сайтом, и человек, ошибшийся дверью, не должен искать ссылку.
 */
export function LoginScreen(): ReactNode {
  const app = useApp()
  const navigate = useNavigate()

  const [mode, setMode] = useState<Mode>('login')
  const [login, setLogin] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isBusy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isRegister = mode === 'register'
  // Ограничения повторяют серверные (RegisterDto): подсказать до
  // отправки дешевле, чем ловить 422. Сервер всё равно проверит сам —
  // это подсказка, а не защита.
  const canSubmit =
    login.trim().length >= 3 &&
    password.length >= (isRegister ? 8 : 1) &&
    (!isRegister || email.includes('@')) &&
    !isBusy

  async function submit(): Promise<void> {
    if (!canSubmit) return
    setBusy(true)
    setError(null)
    try {
      const session = isRegister
        ? await api.register(login.trim(), email.trim(), password)
        : await api.login(login.trim(), password)
      app.signIn(session.user, session.token)
      navigate('/profile')
    } catch (failure) {
      setError(
        failure instanceof Error ? failure.message : 'Не получилось'
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={styles.screen}>
      <ScreenHeader
        title={isRegister ? 'Регистрация' : 'Вход'}
        subtitle="Каталог открыт и без входа — он нужен для обмена"
      />

      <Segmented
        options={MODES}
        value={mode}
        onChange={(next) => {
          setMode(next)
          setError(null)
        }}
        ariaLabel="Вход или регистрация"
      />

      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
      >
        <TextField
          label="Логин"
          value={login}
          onChange={setLogin}
          placeholder="collector"
          autoComplete="username"
          hint={isRegister ? 'От трёх символов. По нему вас найдут в обмене' : undefined}
        />

        {isRegister && (
          <TextField
            label="Почта"
            value={email}
            onChange={setEmail}
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
            hint="Нужна только чтобы восстановить доступ"
          />
        )}

        <TextField
          label="Пароль"
          value={password}
          onChange={setPassword}
          type="password"
          autoComplete={isRegister ? 'new-password' : 'current-password'}
          hint={isRegister ? 'От восьми символов' : undefined}
        />

        <Button kind="primary" type="submit" disabled={!canSubmit}>
          {isBusy
            ? 'Минуту…'
            : isRegister
              ? 'Зарегистрироваться'
              : 'Войти'}
        </Button>

        {error !== null && <div className={styles.failure}>{error}</div>}
      </form>
    </div>
  )
}
